<template>
  <article class="marketplace-row">
    <figure>
      <a :href="`/sku/${sku}`">
        <slot name="image"></slot>
      </a>
    </figure>
    <div class="marketplace-row--info">
      <a :href="`/sku/${sku}`" class="c-gray-900">
        <h3 v-if="title" class="marketplace-row--title">
          {{ title }}
        </h3>
      </a>
      <p v-if="description" class="marketplace-row--description text-base fw-book c-gray-700">{{ description }}</p>
      <p v-if="sku || productType">
        <span v-if="sku">{{ 'Code' | t }} {{ sku }}</span> <span v-if="productType">{{ productType && sku ? ('| ' + productType) :  productType }}</span>
      </p>
    </div>
    <div :class="{
      'marketplace-row--details': true,
      'marketplace-row--details-subscription': subscriptionId
    }">
      <div class="marketplace-row--price-and-quantity">
        <div v-if="price" class="marketplace-row--price">
          <span class="text-base fw-book c-gray-900">{{ price }}</span>
          <span class="marketplace-row--price-label text-base fw-book c-gray-900">{{ uom }}</span>
          <span v-if="subscriptionId" class="inline-block ml-sm text-base fw-strong c-green-400">
            {{ subscriptionPercentageDiscount }}% {{ 'savings' | t }}
          </span>
        </div>
        <div v-if="quantity" class="marketplace-row--quantity">
          <span class="marketplace-row--quantity-label text-base fw-book c-gray-900">Qty</span>
          <span class=" text-base fw-book c-gray-900">{{ quantity }}</span>
        </div>
        <div v-if="subscriptionId">
          <span class="text-base fw-book c-gray-900">Frq: {{ subscriptionFrequency }}</span>
        </div>
        <div v-if="total" class="marketplace-row--total">
          <span class="text-base fw-strong c-gray-900">{{ total }}</span>
        </div>
      </div>
      <div v-if="subscriptionId">
        <div class="flex items-center justify-end gap-xs c-blue-400">
          <SloIcon name="ArrowsRotate" />
          <span class="text-sm fw-strong">{{ 'subscription' | t }}</span>
        </div>
        <slot name="subscription-update" />
      </div>
      <ReorderItemButton v-if="showReorderButton" :product-code="sku" :quantity="quantity" />
    </div>
    <div v-if="$slots.returns" class="marketplace-row--return-requests">
      <slot name="returns" />
    </div>
    <div v-if="$slots.warnings" class="marketplace-row--warnings">
      <slot name="warnings" />
    </div>
    <div v-if="$slots['subscription-next-shipment']" class="marketplace-row--subscription-ship-date">
      <slot name="subscription-next-shipment" />
    </div>
  </article>
</template>

<script>
import t from '../../main/i18n';
import SloButton from '../../../lib/components/SloButton.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import ReorderItemButton from './ReorderItemButton.vue';

export default {
  name: 'MarketplaceRow',
  components: {
    SloButton,
    SloIcon,
    ReorderItemButton
  },
  filters: {
    t
  },
  props: {
    title: {type: String, required: false},
    description: {type: String, required: false},
    sku: {type: String, required: false},
    productType: {type: String, required: false},
    price: {type: String, required: false},
    uom: {type: String, required: false},
    quantity: {type: [Number, String], required: false},
    total: {type: String, required: false},
    showReorderButton: {type: Boolean, required: false, default: false},
    subscriptionId: {type: String|Number, required: false},
    subscriptionFrequency: {type: String, required: false},
    subscriptionPercentageDiscount: {type: String|Number, required: false}
  }
};
</script>

<style lang="scss">
@import '../../../scss/variables';
@import '../../../scss/system';

.marketplace-row {
  display: grid;
  grid-template-columns: moz-fit-content(90px) 1fr 1fr 1fr;
  grid-template-columns: fit-content(90px) 1fr 1fr 1fr;
  grid-template-rows: 1fr auto;
  gap: 1.6rem;

  @media (min-width: $screen-sm) {
    grid-template-columns: moz-fit-content(90px) 1fr 1fr;
    grid-template-columns: fit-content(90px) 1fr 1fr;
    align-items: stretch;
    justify-content: start;
  }

  @media (min-width: $screen-md) {
    grid-template-rows: none;
  }

  figure {
    grid-column: 1 / 2;
    min-width: 90px;
    max-width: 90px;
    max-height: 90px;

    img {
      width: 100%;
      height: auto;
    }
  }

  &--title {
    @include text('base', 'strong', false);
    margin: 0;
  }

  &--info {
    grid-column: 2 / -1;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;

    @media (min-width: $screen-sm) {
      gap: 0.8rem;
    }

    @media (min-width: $screen-md) {
      grid-column: 2 / 3;
    }

    p, span {
      margin: 0;
      color: $color-gray-800;
    }

    span {
      @include text('sm');
    }
  }

  &--details {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    align-items: end;
    justify-content: space-between;
    gap: 1.6rem;

    @media (min-width: $screen-md) {
      grid-column: 3 / -1;
    }

    &.marketplace-row--details-subscription {
      justify-content: flex-start;

      > div {
        justify-content: flex-end;
      }
    }
  }

  &--return-requests,
  &--warnings,
  &--subscription-ship-date {
    grid-column: 1 / -1;
    display: grid;
    gap: 1.6rem;
  }

  &--details-subscription {
    gap: 0.8rem;
  }

  &--price-and-quantity {
    display: flex;
    flex-wrap: wrap;
    column-gap: 1.6rem;
    row-gap: 0.8rem;

    @media (min-width: $screen-sm) {
      justify-content: end;
    }
  }

  &--price, &--quantity {
    display: flex;
    gap: 0.4rem;
  }

  &--total {
    font-weight: 500;
    grid-column-start: 2;
    grid-row-end: 2;
    place-self: end;

    @media (min-width: $screen-sm) {
      grid-column: auto;
      grid-row: auto;
      place-self: start;
    }
  }

  @media print {
    break-inside: avoid;
  }
}

</style>
